<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center flex-wrap gap-2 mb-4">
          <div>
            <h1 class="text-h4 mb-0">{{ $t('passiveOperations.title') }}</h1>
            <p v-if="documentFilter" class="text-body-2 text-medium-emphasis mt-1 mb-0">
              {{ $t('passiveOperations.filteredByDocument', { type: documentTypeLabel, id: documentId }) }}
              <router-link :to="{ path: '/passive-operations', query: currentQuery }" class="ml-2">
                {{ $t('passiveOperations.showAll') }}
              </router-link>
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <div class="mb-4">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filterDateFrom"
                :label="$t('passiveOperations.dateFrom')"
                type="date"
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filterDateTo"
                :label="$t('passiveOperations.dateTo')"
                type="date"
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filterInvestor"
                :items="investors"
                item-title="name"
                item-value="id"
                :label="$t('passiveOperations.investorFilter')"
                clearable
                density="compact"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-btn-toggle v-model="datePeriod" mandatory class="period-buttons">
                <v-btn value="day" size="small">{{ $t('passiveOperations.day') }}</v-btn>
                <v-btn value="week" size="small">{{ $t('passiveOperations.week') }}</v-btn>
                <v-btn value="month" size="small">{{ $t('passiveOperations.month') }}</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-table density="compact" class="no-border-table">
          <thead>
            <tr>
              <th class="px-4 py-3">{{ $t('passiveOperations.operatedAt') }}</th>
              <th class="px-4 py-3">{{ $t('passiveOperations.investor') }}</th>
              <th class="px-4 py-3">{{ $t('passiveOperations.documentType') }}</th>
              <th class="px-4 py-3">{{ $t('passiveOperations.documentId') }}</th>
              <th class="px-4 py-3 text-right">{{ $t('passiveOperations.amountUsdt') }}</th>
              <th class="px-4 py-3">{{ $t('passiveOperations.operationKind') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="op in operations" :key="op.id">
              <td>{{ formatDateTime(op.operated_at) }}</td>
              <td>{{ op.investor_name }}</td>
              <td>{{ documentTypeLabelFor(op.document_type) }}</td>
              <td>
                <router-link v-if="documentLink(op).path !== '#'" :to="documentLink(op)" class="text-primary text-decoration-none">
                  {{ op.document_id }}
                </router-link>
                <span v-else>{{ op.document_id }}</span>
              </td>
              <td class="text-right" :class="amountClass(op.amount_usdt)">
                {{ formatAmount(op.amount_usdt) }}
              </td>
              <td>{{ operationKindLabel(op.operation_kind) }}</td>
            </tr>
            <tr v-if="operations.length === 0 && !loading">
              <td colspan="6" class="text-center text-grey py-6">
                {{ documentFilter ? $t('passiveOperations.noOperationsForDocument') : $t('passiveOperations.noOperations') }}
              </td>
            </tr>
            <tr v-if="loading">
              <td colspan="6" class="text-center py-6">
                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="operations.length > 0 && !loading">
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('passiveOperations.totalPositive') }}
              </td>
              <td class="text-right font-weight-bold text-success pa-3">
                {{ formatAmount(totals.positiveUsdt) }}
              </td>
              <td></td>
            </tr>
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('passiveOperations.totalNegative') }}
              </td>
              <td class="text-right font-weight-bold text-error pa-3">
                {{ formatAmount(totals.negativeUsdt) }}
              </td>
              <td></td>
            </tr>
            <tr class="total-row">
              <td colspan="4" class="text-right font-weight-bold pa-3">
                {{ $t('passiveOperations.totalNet') }}
              </td>
              <td class="text-right font-weight-bold pa-3" :class="amountClass(totals.netUsdt)">
                {{ formatAmount(totals.netUsdt) }}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </v-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet } from '../utils/api'
import { getDateRangeForPeriod } from '../utils/date'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

interface Investor {
  id: number
  name: string
}

interface PassiveOperationRow {
  id: number
  operated_at: string
  document_type: string
  document_id: number
  investor_id: number
  investor_name: string
  amount_usdt: string | number
  operation_kind: string | null
}

const operations = ref<PassiveOperationRow[]>([])
const loading = ref(false)
const investors = ref<Investor[]>([])

const filterDateFrom = ref<string | null>(null)
const filterDateTo = ref<string | null>(null)
const filterInvestor = ref<number | null>(null)
const datePeriod = ref<string>('day')

function documentPath(documentType: string): string {
  switch (documentType) {
    case 'Transaction': return '/transactions'
    case 'Settlement': return '/settlements'
    case 'Expense': return '/expenses'
    case 'CourierTransfer': return '/courier-transfers'
    default: return '#'
  }
}

function documentLink(op: { document_type: string; document_id: number }): { path: string } {
  const base = documentPath(op.document_type)
  if (base === '#') return { path: '#' }
  return { path: `${base}/${op.document_id}` }
}

const documentFilter = computed(() => {
  const type = route.query.document_type as string
  const id = route.query.document_id as string
  return type && id ? { document_type: type, document_id: id } : null
})

const documentTypeLabel = computed(() => {
  if (!documentFilter.value) return ''
  return documentTypeLabelFor(documentFilter.value.document_type)
})

const documentId = computed(() => documentFilter.value?.document_id ?? '')

const currentQuery = computed(() => {
  const q: Record<string, string> = {}
  if (filterDateFrom.value) q.date_from = filterDateFrom.value
  if (filterDateTo.value) q.date_to = filterDateTo.value
  if (filterInvestor.value != null) q.investor_id = String(filterInvestor.value)
  q.period = datePeriod.value
  return q
})

const totals = computed(() => {
  let positiveUsdt = 0
  let negativeUsdt = 0
  for (const op of operations.value) {
    const v = parseFloat(String(op.amount_usdt || 0))
    if (!Number.isFinite(v)) continue
    if (v > 0) positiveUsdt += v
    else if (v < 0) negativeUsdt += v
  }
  return {
    positiveUsdt,
    negativeUsdt,
    netUsdt: positiveUsdt + negativeUsdt
  }
})

function documentTypeLabelFor(type: string): string {
  const key = `walletOperations.docType.${type}`
  const translated = t(key)
  return translated !== key ? translated : type
}

function operationKindLabel(kind: string | null): string {
  if (!kind) return '—'
  const key = `walletOperations.kind.${kind}`
  const translated = t(key)
  return translated !== key ? translated : kind
}

const formatDateTime = (val: string): string => {
  if (!val) return '—'
  const d = new Date(val)
  if (isNaN(d.getTime())) return val
  return d.toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short'
  })
}

const formatAmount = (amount: string | number | null | undefined): string => {
  const value = typeof amount === 'number' ? amount : parseFloat(String(amount || 0))
  if (isNaN(value)) return '0.00'
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })
}

const amountClass = (amount: string | number | null | undefined): string => {
  const value = typeof amount === 'number' ? amount : parseFloat(String(amount || 0))
  if (isNaN(value)) return ''
  return value < 0 ? 'text-error' : value > 0 ? 'text-success' : ''
}

function setDatePeriod(period: string) {
  const range = getDateRangeForPeriod(period, 'sunday')
  filterDateFrom.value = range.from
  filterDateTo.value = range.to
}

function initFromQuery() {
  const q = route.query
  filterDateFrom.value = (q.date_from as string) || null
  filterDateTo.value = (q.date_to as string) || null
  const investorId = q.investor_id != null ? parseInt(String(q.investor_id), 10) : NaN
  filterInvestor.value = Number.isFinite(investorId) ? investorId : null
  const period = (q.period as string) || 'day'
  if (['day', 'week', 'month'].includes(period)) datePeriod.value = period
  if (!q.date_from && !q.date_to) setDatePeriod(datePeriod.value)
}

function syncFiltersToUrl() {
  const query: Record<string, string> = {}
  if (filterDateFrom.value) query.date_from = filterDateFrom.value
  if (filterDateTo.value) query.date_to = filterDateTo.value
  if (filterInvestor.value != null) query.investor_id = String(filterInvestor.value)
  query.period = datePeriod.value
  if (documentFilter.value) {
    query.document_type = documentFilter.value.document_type
    query.document_id = String(documentFilter.value.document_id)
  }
  router.replace({ path: route.path, query })
}

const loadInvestors = async () => {
  const res = await apiGet('/api/investors')
  if (res.ok) {
    const data = await res.json()
    investors.value = Array.isArray(data) ? data : []
  }
}

const loadOperations = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (documentFilter.value) {
      params.set('document_type', documentFilter.value.document_type)
      params.set('document_id', documentFilter.value.document_id)
    }
    if (filterInvestor.value != null) params.set('investor_id', String(filterInvestor.value))
    if (filterDateFrom.value) params.set('date_from', filterDateFrom.value)
    if (filterDateTo.value) params.set('date_to', filterDateTo.value)
    const url = params.toString() ? `/api/passive-operations?${params}` : '/api/passive-operations'
    const res = await apiGet(url)
    if (res.ok) {
      const data = await res.json()
      operations.value = Array.isArray(data) ? data : []
    } else {
      operations.value = []
    }
  } catch {
    operations.value = []
  } finally {
    loading.value = false
  }
}

watch(datePeriod, (period) => setDatePeriod(period))
watch([filterDateFrom, filterDateTo, filterInvestor], syncFiltersToUrl, { deep: true })

onMounted(async () => {
  initFromQuery()
  syncFiltersToUrl()
  await loadInvestors()
  await loadOperations()
})

watch(
  () => [route.query.document_type, route.query.document_id, route.query.date_from, route.query.date_to, route.query.investor_id, route.query.period],
  () => {
    initFromQuery()
    loadOperations()
  }
)
</script>

<style scoped>
.period-buttons :deep(.v-btn) {
  margin: 0;
  padding: 4px 12px;
  min-width: auto;
  height: 24px;
  max-height: 24px;
}

.period-buttons :deep(.v-btn-toggle__content) {
  gap: 0;
}

.total-row {
  background-color: rgba(0, 0, 0, 0.05);
}

.no-border-table :deep(table) {
  border: none;
}
</style>

